import { useState } from 'react';
import { useParams, useNavigate, Link, useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AddressInput, type AddressData } from '@/components/ui/address-input';
import { createGasStation } from '@vn-propane/shared/client';
import type { UserOrganization } from '@vn-propane/shared/client';
import { ArrowLeft } from 'lucide-react';

type OutletContext = {
  organization: UserOrganization;
};

export default function NewGasStation() {
  const { id: orgId } = useParams<{ id: string }>();
  const { t } = useTranslation(['admin', 'common']);
  const navigate = useNavigate();
  const { organization } = useOutletContext<OutletContext>();
  const [saving, setSaving] = useState(false);

  const [name, setName] = useState('');
  const [addressData, setAddressData] = useState<AddressData | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!orgId || !name) return;

    setSaving(true);
    const result = await createGasStation({
      path: { org_id: orgId },
      body: {
        name,
        addressId: addressData?.address?.id || null,
      },
    });

    if (result.error) {
      console.error('Failed to create gas station:', result.error);
      setSaving(false);
      return;
    }

    if (result.data) {
      navigate(`/organizations/${orgId}/stations/${result.data.data.id}`);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-6">
        <Link
          to={`/organizations/${orgId}`}
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="w-4 h-4" />
          {t('gasStation.backToGasStations')}
        </Link>
      </div>

      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight">{t('gasStation.addGasStation')}</h1>
          <p className="text-muted-foreground">
            {t('gasStation.addNewGasStation')}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">{t('gasStation.nameRequired')}</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('gasStation.stationNamePlaceholder')}
                autoFocus
              />
            </div>

            <div className="space-y-2">
              <Label>{t('gasStation.location')}</Label>
              <AddressInput
                organizationId={orgId || ''}
                value={addressData}
                onChange={setAddressData}
                defaultCountry={organization.default_country}
              />
            </div>
          </div>

          <div className="flex gap-4">
            <Button type="submit" disabled={!name || saving}>
              {saving ? t('gasStation.creating') : t('gasStation.createGasStation')}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(`/organizations/${orgId}`)}
            >
              {t('common:buttons.cancel')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
